import { apiCall } from 'src/utils/ApiCall';
import {
  GetControlSummaryDataOutput,
  RecordAttributes,
} from '@amzn/controllershiphub-typescript-client';
import { filterAndCleanRecords } from 'src/components/pages/SummaryPage';
import { CONTROL_APIS, ApiError } from 'src/common/config/ApiConstants';
import { ControlId } from 'src/constants/api';
import {
  LEASE_TYPES,
  LEASE_TYPE_FIELD,
  SDO_EXCLUDED_PRIMARY_USE,
  PRIMARY_USE_FIELD,
} from 'src/constants/grc5253';

/**
 * Gets the leaseType from URL query parameters
 * @returns 'AWS' | 'SDO' | null based on URL - returns null if not explicitly specified
 */
export const getLeaseTypeFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const leaseType = urlParams.get(LEASE_TYPE_FIELD);
  if (leaseType === LEASE_TYPES.SDO) {
    return LEASE_TYPES.SDO;
  } else if (leaseType === LEASE_TYPES.AWS) {
    return LEASE_TYPES.AWS;
  }
  // Return null if leaseType is not explicitly specified in URL
  // This allows backward compatibility - no filter applied by default
  return null;
};

interface DataResponse {
  records: RecordAttributes[][];
  totalRecords: number;
  distinctValues?: Record<string, string[]>;
}

/**
 * Callback invoked after each batch is fetched.
 * Allows the caller to progressively render data as it arrives.
 */
export interface ProgressCallback {
  onBatch: (response: DataResponse & { isComplete: boolean; fetchedSoFar: number }) => void;
}

/**
 * Fetches control data progressively, calling onBatch after each page.
 * The first batch triggers immediate rendering; subsequent batches update in background.
 */
export const getControlDataProgressive = async (
  controlId: string,
  filters: Record<string, string[]> = {},
  progress?: ProgressCallback
): Promise<DataResponse> => {
  try {
    const enhancedFilters = { ...filters };

    if (controlId === ControlId.GRC5253 && !enhancedFilters[LEASE_TYPE_FIELD]) {
      const leaseTypeFromUrl = getLeaseTypeFromUrl();
      if (leaseTypeFromUrl) {
        enhancedFilters[LEASE_TYPE_FIELD] = [leaseTypeFromUrl];
      }
    }

    const queryParams = {
      limit: CONTROL_APIS.BATCH_SIZE.toString(),
      ...(Object.keys(enhancedFilters).length > 0 && {
        filters: JSON.stringify(enhancedFilters),
      }),
    };

    let allRecords: RecordAttributes[][] = [];
    let lastKey: string | undefined;
    let distinctValues: Record<string, string[]> | undefined;

    performance.mark('data-fetch-start');

    do {
      const response = await apiCall<GetControlSummaryDataOutput>({
        endpoint: CONTROL_APIS.ENDPOINTS.DATA(controlId),
        queryParams: lastKey ? { ...queryParams, exclusiveStartKey: lastKey } : queryParams,
      });

      if (response.controlData?.records) {
        allRecords = allRecords.concat(response.controlData.records);
      }

      if (response.distinctValues && !lastKey) {
        distinctValues = response.distinctValues;
      }

      lastKey = response.lastEvaluatedKey;

      if (progress) {
        let cleanedSoFar = filterAndCleanRecords(allRecords);
        if (controlId === ControlId.GRC5253 && getLeaseTypeFromUrl() === LEASE_TYPES.SDO) {
          cleanedSoFar = cleanedSoFar.filter((record) => {
            const primaryUse = record.find((attr) => attr.name === PRIMARY_USE_FIELD)?.value;
            return !SDO_EXCLUDED_PRIMARY_USE.includes(primaryUse ?? '');
          });
        }

        if (!lastKey) {
          performance.mark('all-data-loaded');
          performance.measure('Time to all data', 'data-fetch-start', 'all-data-loaded');
        } else if (allRecords.length <= CONTROL_APIS.BATCH_SIZE) {
          performance.mark('first-data-visible');
          performance.measure('Time to first data', 'data-fetch-start', 'first-data-visible');
        }

        progress.onBatch({
          records: cleanedSoFar,
          totalRecords: cleanedSoFar.length,
          distinctValues,
          isComplete: !lastKey,
          fetchedSoFar: allRecords.length,
        });
      }
    } while (lastKey);

    let cleanedRecords = filterAndCleanRecords(allRecords);

    if (controlId === ControlId.GRC5253 && getLeaseTypeFromUrl() === LEASE_TYPES.SDO) {
      cleanedRecords = cleanedRecords.filter((record) => {
        const primaryUse = record.find((attr) => attr.name === PRIMARY_USE_FIELD)?.value;
        return !SDO_EXCLUDED_PRIMARY_USE.includes(primaryUse ?? '');
      });
    }

    return {
      records: cleanedRecords,
      totalRecords: cleanedRecords.length,
      distinctValues,
    };
  } catch (err: unknown) {
    const error = err as Error;
    if (error instanceof ApiError && error.status === 401) {
      throw new Error(CONTROL_APIS.ERROR_MESSAGES.UNAUTHORIZED);
    }
    throw new Error(CONTROL_APIS.ERROR_MESSAGES.DATA_FETCH_FAILED);
  }
};

/**
 * Fetches the first page of control data immediately.
 * Returns the first batch + a function to fetch remaining pages.
 */
export const getControlDataFirstPage = async (
  controlId: string,
  filters: Record<string, string[]> = {}
): Promise<
  DataResponse & { hasMore: boolean; lastKey?: string; enhancedFilters: Record<string, string[]> }
> => {
  try {
    const enhancedFilters = { ...filters };
    if (controlId === ControlId.GRC5253 && !enhancedFilters[LEASE_TYPE_FIELD]) {
      const leaseTypeFromUrl = getLeaseTypeFromUrl();
      if (leaseTypeFromUrl) {
        enhancedFilters[LEASE_TYPE_FIELD] = [leaseTypeFromUrl];
      }
    }

    const queryParams = {
      limit: CONTROL_APIS.BATCH_SIZE.toString(),
      ...(Object.keys(enhancedFilters).length > 0 && {
        filters: JSON.stringify(enhancedFilters),
      }),
    };

    const response = await apiCall<GetControlSummaryDataOutput>({
      endpoint: CONTROL_APIS.ENDPOINTS.DATA(controlId),
      queryParams,
    });

    let records = response.controlData?.records ?? [];
    let cleanedRecords = filterAndCleanRecords(records);

    if (controlId === ControlId.GRC5253 && getLeaseTypeFromUrl() === LEASE_TYPES.SDO) {
      cleanedRecords = cleanedRecords.filter((record) => {
        const primaryUse = record.find((attr) => attr.name === PRIMARY_USE_FIELD)?.value;
        return !SDO_EXCLUDED_PRIMARY_USE.includes(primaryUse ?? '');
      });
    }

    return {
      records: cleanedRecords,
      totalRecords: cleanedRecords.length,
      distinctValues: response.distinctValues,
      hasMore: !!response.lastEvaluatedKey,
      lastKey: response.lastEvaluatedKey,
      enhancedFilters,
    };
  } catch (err: unknown) {
    const error = err as Error;
    if (error instanceof ApiError && error.status === 401) {
      throw new Error(CONTROL_APIS.ERROR_MESSAGES.UNAUTHORIZED);
    }
    throw new Error(CONTROL_APIS.ERROR_MESSAGES.DATA_FETCH_FAILED);
  }
};

/**
 * Fetches remaining pages after the first page.
 */
export const getControlDataRemainingPages = async (
  controlId: string,
  enhancedFilters: Record<string, string[]>,
  startKey: string
): Promise<DataResponse> => {
  try {
    const queryParams = {
      limit: CONTROL_APIS.BATCH_SIZE.toString(),
      ...(Object.keys(enhancedFilters).length > 0 && {
        filters: JSON.stringify(enhancedFilters),
      }),
    };

    let newRecords: RecordAttributes[][] = [];
    let lastKey: string | undefined = startKey;

    while (lastKey) {
      const response: GetControlSummaryDataOutput = await apiCall<GetControlSummaryDataOutput>({
        endpoint: CONTROL_APIS.ENDPOINTS.DATA(controlId),
        queryParams: { ...queryParams, exclusiveStartKey: lastKey },
      });

      if (response.controlData?.records) {
        newRecords = newRecords.concat(response.controlData.records);
      }
      lastKey = response.lastEvaluatedKey;
    }

    let cleanedRecords = filterAndCleanRecords(newRecords);

    if (controlId === ControlId.GRC5253 && getLeaseTypeFromUrl() === LEASE_TYPES.SDO) {
      cleanedRecords = cleanedRecords.filter((record) => {
        const primaryUse = record.find((attr) => attr.name === PRIMARY_USE_FIELD)?.value;
        return !SDO_EXCLUDED_PRIMARY_USE.includes(primaryUse ?? '');
      });
    }

    return {
      records: cleanedRecords,
      totalRecords: cleanedRecords.length,
    };
  } catch (err: unknown) {
    const error = err as Error;
    if (error instanceof ApiError && error.status === 401) {
      throw new Error(CONTROL_APIS.ERROR_MESSAGES.UNAUTHORIZED);
    }
    throw new Error(CONTROL_APIS.ERROR_MESSAGES.DATA_FETCH_FAILED);
  }
};

/**
 * Original non-progressive version — kept for backward compatibility.
 */
export const getControlData = async (
  controlId: string,
  filters: Record<string, string[]> = {}
): Promise<DataResponse> => {
  return getControlDataProgressive(controlId, filters);
};
